<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            ariaGroupLabel: "Niveau du bâtiment",
            level: {
                groupe: "Groupe d'immeubles",
                immeuble: "Immeuble",
                appartement: "Appartement / Local",
                appartement_pro: "Logement",
                maison: "Immeuble",
                maison_immeuble: "Immeuble",
                maison_appartement: "Logement",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
export type BuildingLevelValue =
    | "groupe"
    | "immeuble"
    | "appartement"
    | "maison"
    | "maison_immeuble"
    | "maison_appartement"

interface LevelOption {
    label: string
    value: BuildingLevelValue
}

export interface Props {
    /**
     * v-model : niveau sélectionné
     */
    modelValue?: string
    /**
     * Niveau du bâtiment parent (réduit les options : ex. parent immeuble → seul appartement proposé)
     */
    parent?: string
    /**
     * Niveau du bâtiment enfant (réduit les options : ex. child appartement → seul immeuble proposé)
     */
    child?: string
    /**
     * Filtre : n'afficher que les options dont la value est dans ce tableau
     */
    only?: string[]
    /**
     * Si true, libellés orientés "pro" (ex. Logement au lieu de Appartement)
     */
    isPro?: boolean
}

interface Emits {
    (e: "update:modelValue", value: string): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelValue: "",
    parent: "",
    child: "",
    only: () => [],
    isPro: false,
})

const emit = defineEmits<Emits>()

// ================================
// HELPERS
// ================================
/**
 * Normalise parent / child (évite tableau ou types inattendus depuis la route).
 */
function coerceLevelContextParam(value: unknown): string {
    if (value == null || value === "") return ""
    if (typeof value === "string") return value
    if (Array.isArray(value)) {
        const first = value[0]
        return typeof first === "string" ? first : ""
    }
    return ""
}

/**
 * Liste `only` fiable (tableau de strings).
 */
function getOnlyValues(only: unknown): string[] {
    if (!Array.isArray(only)) return []
    return only.filter((x): x is string => typeof x === "string")
}

// ================================
// OPTIONS MÉTIER
// ================================
const getLevelOptions = computed<LevelOption[]>(() => {
    const appartementLabel = props.isPro
        ? t("level.appartement_pro")
        : t("level.appartement")
    const fullOptions: LevelOption[] = [
        { value: "groupe", label: t("level.groupe") },
        { value: "immeuble", label: t("level.immeuble") },
        { value: "appartement", label: appartementLabel },
        { value: "maison", label: t("level.maison") },
        { value: "maison_immeuble", label: t("level.maison_immeuble") },
        { value: "maison_appartement", label: t("level.maison_appartement") },
    ]

    const onlyNorm = getOnlyValues(props.only)
    const base: LevelOption[] =
        onlyNorm.length > 0
            ? fullOptions.filter((o) => onlyNorm.includes(o.value))
            : [...fullOptions]

    let options = [...base]
    const parentNorm = coerceLevelContextParam(props.parent)
    const childNorm = coerceLevelContextParam(props.child)

    if (parentNorm) {
        if (["immeuble", "maison_immeuble"].includes(parentNorm)) {
            options = options.filter((o) => o.value === "appartement")
        } else if (parentNorm === "groupe") {
            options = options.filter((o) => o.value === "immeuble")
        }
    }
    if (childNorm) {
        if (["appartement", "maison_appartement"].includes(childNorm)) {
            options = options.filter((o) => o.value === "immeuble")
        } else if (["immeuble", "maison_immeuble"].includes(childNorm)) {
            options = options.filter((o) => o.value === "groupe")
        }
    }

    if (options.length === 0 && base.length > 0) {
        return base
    }
    return options
})

/**
 * Sélection d’un seul niveau à la fois (comportement type radio).
 */
function onSelectLevel(value: string): void {
    emit("update:modelValue", value)
}
</script>

<template>
    <div
        class="building-box-list-level flex flex-wrap gap-2 justify-center"
        role="radiogroup"
        :aria-label="t('ariaGroupLabel')"
    >
        <button
            v-for="option in getLevelOptions"
            :key="option.value"
            type="button"
            role="radio"
            :aria-checked="modelValue === option.value"
            data-cy="option-level"
            class="rounded-lg border border-solid p-3 w-[9.5rem] h-[9.5rem] min-w-[9.5rem] min-h-[9.5rem] max-w-[9.5rem] max-h-[9.5rem] box-border inline-flex items-center justify-center align-top transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0"
            :class="
                modelValue === option.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-surface-200 bg-surface-0 text-surface-900 hover:bg-surface-50'
            "
            @click="onSelectLevel(option.value)"
        >
            <span
                class="flex flex-col items-center justify-center gap-2 h-full min-h-0 w-full min-w-0 text-center pointer-events-none"
            >
                <BaseBuildingIcon
                    class="shrink-0"
                    :type="option.value"
                    :size="32"
                />
                <span
                    class="w-full min-w-0 text-base leading-snug text-balance line-clamp-3 break-words"
                >
                    {{ option.label }}
                </span>
            </span>
        </button>
    </div>
</template>
